import type { Dict } from "@/types/dict"
import type { BasicFormValue } from "./types"
import { isFormField, isFormFieldDefinition, isFormFieldGroup, isFormFieldGroupDefinition, type FormField, type FormFieldDefinition, type FormFieldGroup, type FormFieldGroupDefinition } from "./field"
import type { TDocRef } from "findef"
import { castToState } from "./cast"
import { pick } from "@/utils/object/pick"
import { omit } from "@/utils/object"

export type FormSchema<State extends Dict, Original extends Dict = State, Processed = void, ComputedData extends Dict = Dict> = {
  [k in keyof State]: Exclude<State[k], undefined> extends BasicFormValue
    ? Processed extends true
      ? FormField<Original, ComputedData>
      : FormFieldDefinition<Original, any, ComputedData>
    : Exclude<State[k], undefined> extends Dict | TDocRef<Dict>
      ?
          | (Processed extends true ? FormFieldGroup<Original, State[k], ComputedData> : FormFieldGroupDefinition<Original, State[k], ComputedData>)//FormSchema<State[k], Original, Processed>
          | (Processed extends true ? FormField<Original, ComputedData> : FormFieldDefinition<Original, any, ComputedData>)
      : Processed extends true
        ? FormField<Original, ComputedData>
        : FormFieldDefinition<Original, any, ComputedData>
}

export type ProcessedFormSchema<State extends Dict, ComputedData extends Dict = Dict> = FormSchema<State, State, true, ComputedData>

export const schemaToState = <State extends Dict, ComputedData extends Dict = Dict>(
  schema: FormSchema<State, State, void, ComputedData> | ProcessedFormSchema<State, ComputedData>
): State => {
  const traverse = (obj: any, crumbs: string[] = []): any => {
    const next: any = { ...obj } as any
    for (const [key, value] of Object.entries(obj)) {
      if (
        typeof value === 'undefined' ||
        value === null ||
        typeof value === 'boolean' ||
        typeof value === 'number' ||
        key.startsWith('$')
      )
        continue

      if (isFormFieldGroup(value) || isFormFieldGroupDefinition(value)) {
        next[key] = traverse(value.fields, [...crumbs, key]);
        continue;
      }

      if (isFormFieldDefinition(value) || isFormField(value)) {
        // const path = [...crumbs, key].join('.');
        next[key] = castToState(value.value, value)
        continue
      }

      //if (typeof next[key] === 'object') {
      //  next[key] = traverse(next[key], [...crumbs, key])
      //  continue
      //}
    }
    return next
  }

  return traverse(schema) as State
}

export const getNextFormSchema = <State extends Dict, ComputedData extends Dict = Dict>(
  schema: FormSchema<State, State, void, ComputedData> | ProcessedFormSchema<State>,
  state: State,
  computedData: ComputedData
): ProcessedFormSchema<State, ComputedData> => {
  const traverse = (obj: any, crumbs: string[] = [], groupCrumbs: string[] = [], _skipAutoLabel: boolean = false): any => {
    const next: any = { ...obj } as any
    for (const [key, value] of Object.entries(obj)) {
      if (
        typeof value === 'undefined' ||
        value === null ||
        Array.isArray(value) ||
        typeof value === 'boolean' ||
        typeof value === 'number' ||
        typeof value === 'string' ||
        key.startsWith('$')
      )
        continue

      if (isFormFieldGroup(value) || isFormFieldGroupDefinition(value)) {
        const path = [...crumbs, key].join('.')
        const schemaPath = [...groupCrumbs, key].join('.');
        const nextData = value.attributes?.next
          ? { ...value.attributes || {}, ...value.attributes.next(state, computedData, value.attributes as any) }
          : value.attributes || {};
        next[key] = {
          ...omit(value, ['_isGroupDef']),
          attributes: nextData,
          _isGroup: true,
          path: path,
          schemaPath: schemaPath,
          fields: traverse(value.fields, [...crumbs, key], [...groupCrumbs, key, 'fields'], !!value.attributes?.label),
        };
        continue;
      }

      if (isFormFieldDefinition(value) || isFormField(value)) {
        const path = key === '-' ? crumbs.join('.') : [...crumbs, key].join('.')
        const schemaPath = [...groupCrumbs, key].join('.');

        const nextData = value.next
          ? { ...next[key], ...value.next(state, computedData, {  ...value, value: pick(state, path) }) }
          : { ...next[key], value: pick(state, path) }


        if (key === '-') {
          console.log({ path, schemaPath, key, crumbs, groupCrumbs })
        }
        //const nextValue = nextData.value
        next[key] = {
          ...next[key],
          ...nextData,

          //value: typeof nextData.value === 'undefined' ? value.value : nextData.value,
          _isField: true,
          name: key,
          //...(!skipAutoLabel ? {
          //  label: typeof nextData.label === 'undefined' ? key : nextData.label,
          //} : {}),
          path: path,
          schemaPath: schemaPath
        }

        //console.log(next[key].errors);
        continue
      }

      //if (typeof next[key] === 'object') {
      //
      //  next[key] = traverse(next[key], [...crumbs, key], !!next[key].$attributes?.label)
      //  continue
      //}
    }
    return next
  }

  return traverse(schema) as ProcessedFormSchema<State, ComputedData>
}
